/* eslint-disable @typescript-eslint/no-use-before-define */
import { FunctionComponent, useEffect, useCallback } from 'react'
import { ZendeskAPI } from 'react-zendesk'
import { CHATLAYER, useChatLayerStore } from '.'

const ChatLayer: FunctionComponent = () => {
  const chatLayerInstance = useChatLayerStore(state => state.chatLayerInstance)
  const setChatLayerInstance = useChatLayerStore(
    state => state.setChatLayerInstance,
  )
  const counterSession = useChatLayerStore(state => state.counterSession)
  const setCounterSession = useChatLayerStore(state => state.setCounterSession)

  const sleep = (seconds: number) =>
    new Promise(resolve =>
      setTimeout(() => {
        resolve('')
      }, (seconds * 1000) | 0),
    )

  const sendMessageZendeskchat = useCallback(data => {
    let firstComment = 'PRINCIPAIS INFORMAÇÕES:\n\n'
    for (const [key, value] of Object.entries(data)) {
      firstComment += `${key.charAt(0).toUpperCase()}${key.slice(
        1,
      )}: ${value}\n`
      if (key.trim() === 'atendimento' || key.trim() === 'mensagem') {
        firstComment += '\n'
      }
    }

    ZendeskAPI('webWidget', 'chat:send', firstComment)
  }, [])

  const timeoutHandler = useCallback(
    async data => {
      await sleep(5)
      sendMessageZendeskchat(data)
    },
    [sendMessageZendeskchat],
  )

  const destroyChatlayer = useCallback(() => {
    if (!chatLayerInstance) return

    CHATLAYER.log('Destroying the Chatlayer widget ...')

    // To destroy the widget, it has to be opened first
    chatLayerInstance.open()
    chatLayerInstance.destroy()
    setChatLayerInstance(null)
    setCounterSession(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatLayerInstance])

  const statusZendeskChat = useCallback(() => {
    ZendeskAPI('webWidget:on', 'chat:status', (status: string) => {
      console.log('This chat session is now', status)
    })
  }, [])

  const actionZendeskChat = useCallback(() => {
    ZendeskAPI('webWidget:on', 'userEvent', event => {
      console.log('AÇÕES ZENDESK ', event.category, '-', event.action, event)
    })
  }, [])

  const identifyUserZendeskChat = useCallback(data => {
    const { nome, email } = data
    ZendeskAPI('webWidget', 'identify', {
      name: nome,
      email,
    })
  }, [])

  const tagsZendeskChat = useCallback(ticket => {
    const tags = [...ticket.tags, 'chatbot_sinch']
    ZendeskAPI('webWidget', 'chat:addTags', tags)
  }, [])

  const openZendeskChat = useCallback(() => {
    ZendeskAPI('webWidget:on', 'chat:start', function () {
      console.log('successfully started a Zendesk Chat!')
    })
  }, [])

  const hideZendeskchat = useCallback(() => {
    ZendeskAPI('webWidget', 'close')
    ZendeskAPI('webWidget', 'hide')
  }, [])

  const closeZendeskChat = useCallback(() => {
    ZendeskAPI('webWidget:on', 'chat:end', function () {
      hideZendeskchat()
      onChatLayerLoad()
      console.log('successfully ended a Zendesk Chat session!')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideZendeskchat])

  const showZendeskChat = useCallback(() => {
    ZendeskAPI('webWidget', 'open')
    ZendeskAPI('webWidget', 'show')
  }, [])

  const onChatLayerLoad = useCallback(() => {
    if (chatLayerInstance) return

    CHATLAYER.log('Initializing the Chatlayer widget...')
    const chatlayer = window?.chatlayer(CHATLAYER.config)

    setChatLayerInstance(chatlayer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatLayerInstance])

  useEffect(() => {
    if (!chatLayerInstance) return

    chatLayerInstance.on('event', (event: Record<string, unknown>) => {
      const { data, ticket } = event
      const isChatlayerDestroyEvent = event.action === 'destroy'
      if (isChatlayerDestroyEvent) {
        destroyChatlayer()
        statusZendeskChat()
        actionZendeskChat()
        identifyUserZendeskChat(data)
        tagsZendeskChat(ticket)
        if (counterSession === 0) {
          setCounterSession(counterSession + 1)
          timeoutHandler(data)
        }
        openZendeskChat()
        showZendeskChat()
        closeZendeskChat()
      }
    })
  }, [
    actionZendeskChat,
    chatLayerInstance,
    closeZendeskChat,
    counterSession,
    destroyChatlayer,
    identifyUserZendeskChat,
    openZendeskChat,
    setCounterSession,
    showZendeskChat,
    statusZendeskChat,
    tagsZendeskChat,
    timeoutHandler,
  ])

  useEffect(() => {
    const chatScript = document.querySelector(`[src="${CHATLAYER.url}"]`)

    CHATLAYER.log('checking script')

    if (chatScript) return

    CHATLAYER.log('creating script')
    const script = document.createElement('script')

    script.src = CHATLAYER.url
    script.onload = onChatLayerLoad

    document.body.appendChild(script)
  }, [onChatLayerLoad])

  return null
}

export { ChatLayer }
